/**
 * useTreeUniverse — the FULL classification skeleton for the Assets nav tree.
 *
 * The tree used to derive its branches purely from the assets' solutionMapLinks /
 * topicLinks, so a UC / sales area / industry / topic with NO asset never showed.
 * To list every classification (even empty ones), we fetch the universe:
 *   - GetSolutionMapAll → every industry's full UC matrix (industry / solutionArea
 *     / useCase / solutionMapId). DEPRECATED UCs are dropped to match the read-side
 *     UC Catalog tree (which hides them in view mode) + the Create modal.
 *   - ListTopics → every topic + its sub-topic instances.
 *
 * Resilient by design: if either call fails (or no client is set, e.g. a unit test
 * that only cares about asset-derived branches), the universe is empty and the tree
 * falls back to its old asset-only shape — never blocks or throws.
 */
import { useEffect, useState } from 'react';
import { getIvtClient, GetSolutionMapAllCommand } from '../../../client/IvtHubClient';
import { topicClient } from '../../../client/topicClient';

/** One UC in the solution-map universe (a flattened GetSolutionMapAll entry). */
export interface UcUniverseEntry {
  industry: string;
  solutionArea: string;
  useCase: string;
  solutionMapId: number;
}

export interface TreeUniverse {
  /** Every non-deprecated UC across all industries. */
  ucEntries: UcUniverseEntry[];
  /** Every industry name (incl. ones with no UCs / assets). */
  industries: string[];
  /** Every (topic, sub-topic) instance. */
  subTopics: { topicName: string; subTopicName: string }[];
  /** Every top-level topic name (incl. ones with no instances). */
  topicNames: string[];
}

const EMPTY: TreeUniverse = { ucEntries: [], industries: [], subTopics: [], topicNames: [] };

export function useTreeUniverse(): TreeUniverse {
  const [universe, setUniverse] = useState<TreeUniverse>(EMPTY);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Each source is independent — one failing must not blank the other, so we
      // settle them separately and merge whatever came back. Wrap each in a thunk'd
      // promise so a SYNCHRONOUS throw from getIvtClient() (thrown when the stage has
      // no configured ivtApiEndpoint) is captured as a rejected settle result rather
      // than escaping allSettled as an unhandled rejection — preserving the "never
      // throws, falls back to asset-only" contract in the docstring above.
      const [smRes, topicRes] = await Promise.allSettled([
        Promise.resolve().then(() => getIvtClient().send(new GetSolutionMapAllCommand({}))),
        Promise.resolve().then(() => topicClient.list()),
      ]);
      if (cancelled) return;

      const next: TreeUniverse = { ucEntries: [], industries: [], subTopics: [], topicNames: [] };

      if (smRes.status === 'fulfilled') {
        for (const ind of smRes.value.industries ?? []) {
          if (!ind.industry) continue;
          next.industries.push(ind.industry);
          for (const e of ind.entries ?? []) {
            // Skip rows we can't link/label + DEPRECATED (hidden read-side).
            if (typeof e.id !== 'number' || !e.useCase || e.status === 'DEPRECATED') continue;
            next.ucEntries.push({
              industry: ind.industry,
              solutionArea: e.solutionArea ?? '',
              useCase: e.useCase,
              solutionMapId: e.id,
            });
          }
        }
      }

      if (topicRes.status === 'fulfilled') {
        for (const t of topicRes.value.topics ?? []) {
          if (!t.name) continue;
          next.topicNames.push(t.name);
          for (const inst of t.instances ?? []) {
            if (!inst.subTopicName) continue;
            next.subTopics.push({ topicName: t.name, subTopicName: inst.subTopicName });
          }
        }
      }

      setUniverse(next);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return universe;
}
